import { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export default function Auth() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode]     = useState('login');
  const [form, setForm]     = useState({ email:'', password:'', full_name:'' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess]   = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetErr, setResetErr] = useState('');

  async function handleReset() {
    if (!resetEmail.trim()) return setResetErr('Please enter your email');
    setResetLoading(true); setResetErr(''); setResetMsg('');
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setResetMsg('Check your email for a password reset link.');
    } catch(e) {
      setResetErr(e.message);
    } finally {
      setResetLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      if (mode === 'login') { await signIn(form.email, form.password); }
      else { await signUp(form.email, form.password, form.full_name); setSuccess('Account created! Check your email to confirm, then log in.'); setMode('login'); }
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  }

  const s = {
    wrap: { minHeight:'100vh', background:'#FAF7F2', display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
    card: { background:'white', borderRadius:24, padding:'40px 36px', width:'100%', maxWidth:420, boxShadow:'0 8px 40px rgba(44,26,14,.12)', border:'1px solid #EDE5D8' },
    logo: { fontFamily:'Cormorant Garamond,serif', fontSize:28, color:'#2C1A0E', marginBottom:4 },
    sub:  { fontSize:13, color:'#7A6555', marginBottom:32 },
    label:{ display:'block', fontSize:11, fontWeight:500, letterSpacing:'.07em', textTransform:'uppercase', color:'#7A6555', marginBottom:6 },
    input:{ width:'100%', padding:'10px 13px', border:'1px solid #E5D9C8', borderRadius:10, fontFamily:'Jost,sans-serif', fontSize:14, color:'#2C1A0E', outline:'none', marginBottom:16 },
    btn:  { width:'100%', padding:'12px', background:'#C4612A', color:'white', border:'none', borderRadius:10, fontFamily:'Jost,sans-serif', fontSize:15, fontWeight:500, cursor:'pointer', marginTop:8 },
    toggle:{ textAlign:'center', marginTop:20, fontSize:13, color:'#7A6555' },
    tlink:{ color:'#C4612A', cursor:'pointer', fontWeight:500, background:'none', border:'none', fontFamily:'Jost,sans-serif', fontSize:13 },
    err:  { background:'#FFEBEE', color:'#B71C1C', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 },
    ok:   { background:'#E8F5E9', color:'#1B5E20', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 },
  };

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <h1 style={s.logo}>What can<br/>we bring?</h1>
        <p style={s.sub}>{mode === 'login' ? 'Sign in to your dinner party planner' : 'Create your account'}</p>
        {error && <div style={s.err}>{error}</div>}
        {success && <div style={s.ok}>{success}</div>}
        <form onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <div><label style={s.label}>Full name</label>
              <input style={s.input} value={form.full_name} onChange={e=>setForm(f=>({...f,full_name:e.target.value}))} placeholder="Your name" required/>
            </div>
          )}
          <label style={s.label}>Email</label>
          <input style={s.input} type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="you@example.com" required/>
          <label style={s.label}>Password</label>
          <input style={s.input} type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="••••••••" required minLength={6}/>
          <button style={s.btn} disabled={loading}>{loading ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}</button>
        </form>
        <div style={s.toggle}>
          {mode === 'login' ? <>Don't have an account? <button style={s.tlink} onClick={()=>setMode('signup')}>Sign up</button></>
            : <>Already have an account? <button style={s.tlink} onClick={()=>setMode('login')}>Sign in</button></>}
        </div>

        {mode === 'login' && !showReset && (
          <div style={{textAlign:'center',marginTop:12}}>
            <button style={s.tlink} onClick={()=>setShowReset(true)}>Forgot password?</button>
          </div>
        )}

        {showReset && (
          <div style={{marginTop:20,paddingTop:20,borderTop:'1px solid #E5D9C8'}}>
            <p style={{fontSize:13,color:'#7A6555',marginBottom:12}}>Enter your email and we'll send you a reset link.</p>
            {resetErr && <div style={{background:'#FFEBEE',color:'#B71C1C',padding:'8px 12px',borderRadius:8,fontSize:13,marginBottom:10}}>{resetErr}</div>}
            {resetMsg && <div style={{background:'#E8F5E9',color:'#1B5E20',padding:'8px 12px',borderRadius:8,fontSize:13,marginBottom:10}}>{resetMsg}</div>}
            <input
              style={s.input}
              type="email"
              placeholder="your@email.com"
              value={resetEmail}
              onChange={e=>setResetEmail(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleReset()}
            />
            <div style={{display:'flex',gap:8}}>
              <button style={{...s.btn,background:'#7A6555',flex:1}} onClick={()=>{setShowReset(false);setResetErr('');setResetMsg('');}}>
                Cancel
              </button>
              <button style={{...s.btn,flex:1,opacity:resetLoading?0.6:1}} disabled={resetLoading} onClick={handleReset}>
                {resetLoading ? 'Sending…' : 'Send reset link'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
